import type { CreateCrosspostRequest, SocialPlatform } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Facebook, Instagram, Loader2, type LucideIcon, Send, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useConnections } from '~/entities/crossposting/model/use-connections'
import { useCreateCrosspost } from '~/entities/crossposting/model/use-facebook-post'
import { AiImageButton, AiTextButton } from '~/features/ai-studio/components'
import { ImageCropFrame } from '~/features/crossposting/components/image-crop-frame'
import { cropFileToFormat, ImageCropError } from '~/features/crossposting/lib/crop-image'
import { getNarrowestFormat } from '~/features/crossposting/lib/formats'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Label } from '~/shared/ui/label'
import { Skeleton } from '~/shared/ui/skeleton'
import { Textarea } from '~/shared/ui/textarea'
import { PageHeader } from '~/widgets/page-header'

type CrosspostType = 'post' | 'story'

const PLATFORM_META: Record<SocialPlatform, { label: () => string; icon: LucideIcon }> = {
  facebook: { label: m.crosspostingFacebook, icon: Facebook },
  instagram: { label: m.crosspostingInstagram, icon: Instagram },
}

const TYPES: {
  value: CrosspostType
  label: () => string
  hint: () => string
  maxImages: number
}[] = [
  { value: 'post', label: m.crosspostingTypePost, hint: m.crosspostingTypePostHint, maxImages: 10 },
  {
    value: 'story',
    label: m.crosspostingTypeStory,
    hint: m.crosspostingTypeStoryHint,
    maxImages: 1,
  },
]

interface SelectedImage {
  file: File
  preview: string
  /** Position of the crop frame along the image's slack axis, 0..1. */
  offset: number
}

export function CrosspostTool({ listingId }: { listingId: string }) {
  const { data: business, isLoading: isBusinessLoading } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: connectionData, isLoading: isConnectionsLoading } = useConnections(
    businessId,
    listingId,
  )
  const createCrosspost = useCreateCrosspost(businessId, listingId)

  const [type, setType] = useState<CrosspostType>('post')
  const [selected, setSelected] = useState<SocialPlatform[]>([])
  const [text, setText] = useState('')
  const [overrides, setOverrides] = useState<Record<SocialPlatform, string>>({
    facebook: '',
    instagram: '',
  })
  const [showOverrides, setShowOverrides] = useState(false)
  const [images, setImages] = useState<SelectedImage[]>([])
  const [publishing, setPublishing] = useState(false)
  const captionFieldId = useId()

  const imagesRef = useRef(images)
  imagesRef.current = images
  useEffect(() => {
    return () => {
      for (const image of imagesRef.current) URL.revokeObjectURL(image.preview)
    }
  }, [])

  const connected = (connectionData?.connections ?? [])
    .filter((c) => c.status === 'connected')
    .map((c) => c.platform)

  const initializedRef = useRef(false)
  useEffect(() => {
    if (!initializedRef.current && connected.length > 0) {
      initializedRef.current = true
      setSelected(connected)
    }
  }, [connected])

  const activeType = TYPES.find((t) => t.value === type) ?? TYPES[0]
  const maxImages = activeType.maxImages
  const format = getNarrowestFormat(selected, type)

  const addImage = (file: File) => {
    const preview = URL.createObjectURL(file)
    setImages((prev) => {
      if (maxImages === 1) {
        for (const image of prev) URL.revokeObjectURL(image.preview)
        return [{ file, preview, offset: 0.5 }]
      }
      if (prev.length >= maxImages) {
        URL.revokeObjectURL(preview)
        toast.error(m.crosspostingMaxImages({ count: String(maxImages) }))
        return prev
      }
      return [...prev, { file, preview, offset: 0.5 }]
    })
  }

  const removeImage = (index: number) => {
    setImages((prev) => {
      const target = prev[index]
      if (target) URL.revokeObjectURL(target.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const setImageOffset = (index: number, offset: number) => {
    setImages((prev) => prev.map((image, i) => (i === index ? { ...image, offset } : image)))
  }

  const selectType = (next: CrosspostType) => {
    setType(next)
    const nextMax = TYPES.find((t) => t.value === next)?.maxImages ?? 1
    if (nextMax < images.length) {
      setImages((prev) => {
        for (const image of prev.slice(nextMax)) URL.revokeObjectURL(image.preview)
        return prev.slice(0, nextMax)
      })
    }
  }

  const togglePlatform = (platform: SocialPlatform) => {
    setSelected((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform],
    )
  }

  const reset = () => {
    for (const image of images) URL.revokeObjectURL(image.preview)
    setText('')
    setOverrides({ facebook: '', instagram: '' })
    setImages([])
  }

  const canPublish = selected.length > 0 && images.length > 0 && !publishing

  const publish = async () => {
    setPublishing(true)
    try {
      const facebookBlock =
        selected.includes('facebook') && overrides.facebook.trim()
          ? { text: overrides.facebook.trim() }
          : undefined
      const instagramBlock =
        selected.includes('instagram') && overrides.instagram.trim()
          ? { text: overrides.instagram.trim() }
          : undefined

      const payload: CreateCrosspostRequest = {
        platforms: selected,
        type,
        text: text.trim() || undefined,
        facebook: facebookBlock,
        instagram: instagramBlock,
      }
      const files = await Promise.all(
        images.map((image) => cropFileToFormat(image.file, image.preview, format, image.offset)),
      )

      const { posts } = await createCrosspost.mutateAsync({ payload, files })
      toast.success(m.crosspostingCrossPublished({ count: String(posts.length) }))
      reset()
    } catch (error) {
      toast.error(
        error instanceof ImageCropError
          ? m.crosspostingImageUnprocessable()
          : getErrorMessageFromError(error),
      )
    } finally {
      setPublishing(false)
    }
  }

  const backLink = (
    <Button asChild variant="ghost" size="sm" className="-ml-2 w-fit">
      <Link to="/dashboard/crossposting/$listingId" params={{ listingId }}>
        <ArrowLeft className="h-4 w-4" />
        {m.crosspostingBackToTools()}
      </Link>
    </Button>
  )

  if (isBusinessLoading || isConnectionsLoading) {
    return (
      <div className="space-y-6">
        {backLink}
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    )
  }

  if (connected.length === 0) {
    return (
      <div className="space-y-6">
        {backLink}
        <PageHeader
          title={m.crosspostingCrossPostTitle()}
          description={m.crosspostingCrossPostDescription()}
        />
        <Card className="flex flex-col items-start gap-3 p-6">
          <p className="text-muted-foreground text-sm">{m.crosspostingNoConnectedAccounts()}</p>
          <Button asChild>
            <Link to="/dashboard/crossposting/$listingId/connect" params={{ listingId }}>
              {m.crosspostingConnect()}
            </Link>
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {backLink}
      <PageHeader
        title={m.crosspostingCrossPostTitle()}
        description={m.crosspostingCrossPostDescription()}
      />

      <Card className="space-y-5 p-6">
        <div className="space-y-2">
          <Label>{m.crosspostingPlatforms()}</Label>
          <p className="text-muted-foreground text-xs">{m.crosspostingPlatformsHint()}</p>
          <div className="flex flex-wrap gap-2">
            {connected.map((platform) => {
              const meta = PLATFORM_META[platform]
              const Icon = meta.icon
              const isActive = selected.includes(platform)
              return (
                <button
                  key={platform}
                  type="button"
                  onClick={() => togglePlatform(platform)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'border-brand bg-brand-subtle text-brand-subtle-foreground'
                      : 'hover:bg-accent',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {meta.label()}
                </button>
              )
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{m.crosspostingPostType()}</Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {TYPES.map((option) => {
              const isActive = option.value === type
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => selectType(option.value)}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-colors',
                    isActive
                      ? 'border-brand bg-brand-subtle text-brand-subtle-foreground'
                      : 'hover:bg-accent',
                  )}
                >
                  <span className="font-medium text-sm">{option.label()}</span>
                  <p className="mt-1 text-muted-foreground text-xs">{option.hint()}</p>
                </button>
              )
            })}
            <div className="flex cursor-not-allowed items-start rounded-lg border p-3 opacity-60">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{m.crosspostingTypeReel()}</span>
                  <Badge variant="secondary">{m.crosspostingComingSoonTitle()}</Badge>
                </div>
                <p className="mt-1 text-muted-foreground text-xs">{m.crosspostingTypeReelHint()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={captionFieldId}>{m.crosspostingCaption()}</Label>
            <AiTextButton kind={`${selected[0] ?? 'facebook'}-post`} onUse={setText} />
          </div>
          <Textarea
            id={captionFieldId}
            rows={5}
            maxLength={2200}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={m.crosspostingCaptionPlaceholder()}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2"
            onClick={() => setShowOverrides((v) => !v)}
          >
            {m.crosspostingCustomizePerPlatform()}
          </Button>
          {showOverrides &&
            selected.map((platform) => (
              <div key={platform} className="space-y-1">
                <Label className="text-muted-foreground text-xs">
                  {PLATFORM_META[platform].label()}
                </Label>
                <Textarea
                  rows={3}
                  maxLength={platform === 'facebook' ? 5000 : 2200}
                  value={overrides[platform]}
                  onChange={(e) =>
                    setOverrides((prev) => ({ ...prev, [platform]: e.target.value }))
                  }
                  placeholder={m.crosspostingOverridePlaceholder()}
                />
              </div>
            ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{m.crosspostingImages()}</Label>
            <AiImageButton onUse={addImage} orientation="portrait" />
          </div>

          {images.length > 0 && (
            <div className="space-y-3">
              {images.map((image, index) => (
                <div key={image.preview} className="group relative">
                  <ImageCropFrame
                    previewUrl={image.preview}
                    format={format}
                    offset={image.offset}
                    onOffsetChange={(offset) => setImageOffset(index, offset)}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute top-1 right-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {images.length < maxImages && (
            <ImageUploadField
              file={null}
              preview={null}
              compact
              onSelect={(file) => file && addImage(file)}
              subHint={m.crosspostingMaxImages({ count: String(maxImages) })}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs">{activeType.hint()}</p>
          <Button onClick={publish} disabled={!canPublish}>
            {publishing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {m.crosspostingPublishCross()}
          </Button>
        </div>
      </Card>
    </div>
  )
}
